import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Grid, Divider, Paper, InputAdornment, Button } from '@material-ui/core';
import DownloadUrl from './DownloadUrl';
import { isEmpty, isNil } from 'lodash';
import { ArtifactSizeInfo } from './ArtifactSizeInfo';
import ArtifactViewerDialog from '../../common/ApprovalViewer/ArtifactViewerDialog';
import { formatBytes, getLocalDateTime } from '../../../Utils/GlobalUtils';
import TextField from '@material-ui/core/TextField';
import SearchIcon from '@material-ui/icons/Search';
import ArtifactTerminalStatusIndicator from '../Sources/ArtifactTerminalStatusIndicator';
import ArtifactBuildError from './ArtifactBuildError';
import ArtifactStatusDropdownContainer from '../ArtifactStatusDropdownContainer';
import QRCodeDialog from './QrCodeDialog';
import _ from 'lodash';
import { artifactStatusMap } from '../../../Constants/Files';
import { isTerminalStatus } from '../../../Utils/FileUtils';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import ArtifactRebuildBtn from '../Sources/ArtifactRebuildBtn';
import { assetPlatforms } from '../../../Constants/Asset';
import PermalinkCopyBtn from '../Sources/SignedUrlCopyBtn';
import ArtifactOverwriteBtn from "../Sources/ArtifactOverwriteBtn";

const useStyles = makeStyles((theme) => ({
	root: {
		width: '100%',
	},
	accordion: {
		backgroundColor: '#F5F5F5',
	},
	heading: {
		fontSize: theme.typography.pxToRem(30),
		fontWeight: theme.typography.fontWeightBold,
	},
	card: {
		flexGrow: 1,
	},
	paper: {
		padding: theme.spacing(2),
		margin: '10px',
		minWidth: '21em',
	},
	headerTypography: {
		color: theme.palette.neutral.n400,
		textTransform: 'uppercase',
	},
	textTransformNone: {
		textTransform: 'none',
	},
	fileName: {
		wordBreak: 'break-all',
	},
	artifactAvatar: {
		width: '7.5em',
		height: '7.5em',
	},
	searchForm: {
		width: '40em',
	},
	search: {
		margin: theme.spacing(1),
		width: '15em',
	},
	showHide: {
		padding: '1.5em',
	},
	liveStatusContainer: {
		'& button': {
			width: '100%',
			justifyContent: 'center',
			marginTop: '-2em',
			border: 'none',
		},
	},
	formControl: {
		margin: theme.spacing(1),
	},
	sortBySelect: {
		minWidth: 170,
	},
	selectEmpty: {
		marginTop: theme.spacing(2),
	},
}));

const sortByFields = {
	'Latest build': 'buildNumber',
	Status: 'status',
};

const ArtifactFieldVal = ({ label, val }) => {
	return (
		<Typography variant="body1">
			<span style={{ fontWeight: 'bold' }}>{label}: </span>
			{val}
		</Typography>
	);
};
const ArtifactDownloadBtn = ({ url, error, type }) => {
	// Disable download button when build failed for an artifact.
	const isBtnDisabled = !isEmpty(error);
	const toolTipMsg = !isEmpty(error) ? 'Artifact Build failed' : '';
	return <DownloadUrl downloadUrl={url} isBtnDisabled={isBtnDisabled} toolTipMsg={toolTipMsg} label={type}></DownloadUrl>;
};
// Artifact card component, displayed part of source file container.
const ArtifactCard = (props) => {
	const classes = useStyles();
	const { asset, artifact, sourceFile } = props;
	const { size, type, url, createdAt, updatedAt, error, imageUrl, buildNumber } = artifact;
	const qrUrl = isNil(url) ? '' : url;
	const createdDate = getLocalDateTime(createdAt);
	const updatedDate = getLocalDateTime(updatedAt);

	return (
		<Grid container className={classes.card}>
			<Paper className={classes.paper}>
				<Grid container style={{ height: '30em' }}>
					<Grid item xs={12} className={classes.liveStatusContainer}>
						<ArtifactTerminalStatusIndicator artifact={artifact} platform={assetPlatforms.lowes} />
					</Grid>
					<Grid item xs={12}>
						<Grid container alignItems="center" spacing={2}>
							<Grid item xs={6}>
								<ArtifactViewerDialog
									label={`Artifact - ${type}`}
									sourceModelUrl={sourceFile.url}
									artifactModelUrl={url}
									size={formatBytes(size)}
									type={type}
									srcFile={sourceFile}
									artifact={artifact}
									asset={asset}
									imageUrl={imageUrl}
								/>
							</Grid>
							<Grid item xs={6}>
								<QRCodeDialog url={qrUrl} type={type} />
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12}>
						<Grid container>
							<Grid item xs={5}>
								<Typography className={classes.headerTypography} variant="h4">
									{type}
								</Typography>
							</Grid>

							<Grid item xs={5}>
								<Grid container>
									<ArtifactStatusDropdownContainer
										asset={asset}
										sourceFile={sourceFile}
										artifact={artifact}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>

					<Grid item xs={12} container>
						<ArtifactSizeInfo bytes={size} />
					</Grid>
					<Grid item xs={12} container>
						<Grid container>
							<Grid item xs={8}>
								<ArtifactFieldVal label="Build Number" val={buildNumber} />
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12} container>
						<Grid container>
							<Grid item xs={9}>
								<ArtifactFieldVal label="Build Date" val={createdDate} />
							</Grid>
							<Grid item xs={3}>
								<ArtifactBuildError error={error} />
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12} container>
						<ArtifactFieldVal label="Updated Date" val={updatedDate} />
					</Grid>
				</Grid>
				<Divider />
				<Grid container alignItems="center">
					<Grid item xs={3}>
						<ArtifactRebuildBtn artifact={artifact} />
					</Grid>
					<Grid item xs={4}>
						<ArtifactOverwriteBtn artifact={artifact} />
					</Grid>
					<Grid item xs={3}>
						<ArtifactDownloadBtn url={url} error={error} type={type} />
					</Grid>
					<Grid item xs={2}>
						<PermalinkCopyBtn permalink={url} />
					</Grid>
				</Grid>
			</Paper>
		</Grid>
	);
};

export default function ArtifactAccordion({ asset, srcFile, artifacts }) {
	const classes = useStyles();
	const [showAll, setShowHide] = useState(false);
	const [hidden, setHidden] = useState(0);
	const [searchTerm, setSearchTerm] = useState('');
	const [filteredArtifacts, setFilteredArtifacts] = useState([]);
	// status is the Default sort dropdown option.
	const [sortBy, setSortBy] = useState(sortByFields.Status);
	useEffect(() => {
		// sort artifacts by terminal statuses and build number.
		artifacts.sort((a, b) => {
			if (sortBy === sortByFields.Status) {
				if (isTerminalStatus(a.status)) {
					return -1;
				} else if (isTerminalStatus(b.status)) {
					return 1;
				}
			}
			return b[sortBy] - a[sortBy];
		});
		let filteredData = artifacts;
		// Only filter data when Hide Rejected button is clicked.
		if (!showAll) {
			filteredData = _.filter(artifacts, (item) => {
				return item.status !== artifactStatusMap.rejected.value;
			});
			const count = _.filter(artifacts, (item) => {
				return item.status === artifactStatusMap.rejected.value;
			}).length;
			setHidden(count);
		}
		setFilteredArtifacts(filteredData);
	}, [artifacts, sortBy, showAll]);
	// getting total artifacts count for the sourceFile.
	const artifactsCount = artifacts.length;
	// Search filter text box. to search across all artifacts of an source file.
	const AccordionFilter = () => {
		const filter = (term, show) => {
			let filteredData = artifacts.filter((artifact) => {
				return Object.keys(artifact).some((key) => {
					return artifact[key]?.toString().toLowerCase().includes(term.toLowerCase());
				});
			});
			if (!show) {
				filteredData = _.filter(filteredData, (item) => {
					return item.status !== artifactStatusMap.rejected.value;
				});
			}
			const count = _.filter(artifacts, (item) => {
				return item.status === artifactStatusMap.rejected.value;
			}).length;
			setHidden(count);
			setFilteredArtifacts(filteredData);
		};

		const handleShowHide = (show) => {
			setShowHide(show);
			filter(searchTerm, show);
		};
		const handleChange = (event) => {
			const term = event.target.value;
			setSearchTerm(term);
			filter(term, showAll);
		};
		return (
			<form noValidate autoComplete="off" className={classes.searchForm}>
				<TextField
					id={'accordion-search-' + srcFile.id}
					label="Search"
					className={classes.search}
					value={searchTerm}
					onChange={handleChange}
					variant="outlined"
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon />
							</InputAdornment>
						),
					}}
				/>
				<FormControl variant="outlined" className={classes.formControl}>
					<InputLabel htmlFor="outlined-sortby-simple"> Sort by </InputLabel>{' '}
					<Select
						native
						value={sortBy}
						onChange={(event) => setSortBy(event.target.value)}
						label="Sort by"
						className={classes.sortBySelect}
						inputProps={{
							name: 'sortby',
							id: 'sortby-select',
						}}
					>
						{Object.keys(sortByFields).map((key) => {
							const sortBy = sortByFields[key];
							return (
								<option value={sortBy} key={sortBy}>
									{key}
								</option>
							);
						})}
					</Select>
				</FormControl>
				{showAll ? (
					<Button
						className={classes.showHide}
						onClick={() => {
							handleShowHide(false);
						}}
						size="large"
						color="primary"
					>
						Hide Rejected
					</Button>
				) : (
					<Button
						className={classes.showHide}
						onClick={() => {
							handleShowHide(true);
						}}
						size="large"
						color="primary"
					>
						Show Rejected ({hidden})
					</Button>
				)}
			</form>
		);
	};
	return (
		<div className={classes.root}>
			<Grid container alignItems="center">
				<Grid item xs={4}>
					<Typography className={classes.heading}>Artifacts - {artifactsCount} File(s)</Typography>
				</Grid>
				<Grid item xs={2}>
					{AccordionFilter()}
				</Grid>
				<Grid container>
					{filteredArtifacts.map((artifact) => {
						const artifactCardProps = {
							asset,
							artifact,
							sourceFile: srcFile,
						};
						return (
							<Grid item xs={6} key={artifact.id}>
								<ArtifactCard {...artifactCardProps} />
							</Grid>
						);
					})}
				</Grid>
			</Grid>
		</div>
	);
}
